/**
 * Shared pieces of the order screens (Get it built — docs/marketplace_get_it_built.md):
 * the context every tab receives, labels by role, the money bar and small helpers.
 * Every number shown comes from the order (terms, stages, money) — never a literal.
 */
import { fmtDate, money } from '@/api/marketplace'
import {
  STAGE_STATUS_LABELS,
  type Measured, type Monthly, type Order, type OrderStage, type StageStatus,
} from '@/api/marketplaceBuild'

export type TabId = 'overview' | 'stages' | 'reports' | 'payments' | 'messages'
export const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'stages', label: 'Stages' },
  { id: 'reports', label: 'Progress reports' },
  { id: 'payments', label: 'Payments' },
  { id: 'messages', label: 'Messages' },
]

/** What every order tab gets from the page. */
export interface OrderCtx {
  order: Order
  busy: boolean
  /** Run an order action; the returned Order replaces the screen's copy. Null on failure (already shown). */
  act: (fn: () => Promise<Order>, ok: string) => Promise<Order | null>
  setOrder: (o: Order) => void
  reload: () => Promise<void>
  goTab: (t: TabId) => void
  /** The signed-in user's id (to tell my messages from theirs). */
  me?: string
}

export const STAGE_TONE: Record<StageStatus, string> = {
  pending: '', payment_due: 'bad', in_review: 'warn', changes_requested: 'warn',
  approved: 'info', released: 'ok', disputed: 'bad', refunded: '',
}

export function stageLabel(s: OrderStage, role: Order['role']): string {
  if (role === 'builder') {
    if (s.status === 'released') return 'Paid to you'
    if (s.status === 'in_review') return 'Waiting for the client'
  }
  if (role === 'client' && s.status === 'in_review') return 'Waiting for your approval'
  return STAGE_STATUS_LABELS[s.status]
}

export function StagePill({ stage, role }: { stage: OrderStage; role: Order['role'] }) {
  return <span className={`mkt-pill ${STAGE_TONE[stage.status]}`}>{stageLabel(stage, role)}</span>
}

/** "Every screen; Service process\nData model" → one item each (bullets stripped). */
export function splitDeliverables(text: string | null | undefined): string[] {
  return (text || '')
    .split(/\r?\n|;/)
    .map((x) => x.replace(/^\s*(?:[-*•·]|\d+[.)])\s*/, '').trim())
    .filter(Boolean)
}

export function firstName(person: string | null | undefined, fallback = 'the builder'): string {
  const p = (person || '').trim()
  return p ? p.split(/\s+/)[0] : fallback
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${fmtDate(iso)}, ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`
}

export function addDays(iso: string | null | undefined, days: number): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  d.setDate(d.getDate() + days)
  return d.toISOString()
}

export function hasMonthly(m: Monthly | Record<string, never> | null | undefined): m is Monthly {
  return !!m && typeof (m as Monthly).price_minor === 'number' && (m as Monthly).price_minor > 0
}

/** "₹240 per person a month" / "₹12,900 a month (Small server)". */
export function monthlyText(m: Monthly | Record<string, never> | null | undefined, currency: string): string {
  if (!hasMonthly(m)) return ''
  return m.basis === 'person'
    ? `${money(m.price_minor, currency)} per person a month`
    : `${money(m.price_minor, currency)} a month${m.size ? ` (${m.size} server)` : ''}`
}

/** Leading stages already paid out to the builder (PaymentStages' `done`). */
export function doneCount(order: Order): number {
  let n = 0
  for (const s of order.stages) {
    if (s.status !== 'released') break
    n += 1
  }
  return n
}

export function currentStage(order: Order): OrderStage | null {
  if (order.current_idx == null) return null
  return order.stages.find((s) => s.idx === order.current_idx) ?? null
}

export function isLast(order: Order, s: OrderStage): boolean {
  return order.stages.length > 0 && s.idx === order.stages[order.stages.length - 1].idx
}

export function liveBy(order: Order): string | null {
  const last = order.stages[order.stages.length - 1]
  return last?.target_date ?? null
}

/** Paid to the builder | held by Twasta | still to pay — one stacked bar over the price. */
export function MoneyBar({ order }: { order: Order }) {
  const m = order.money
  const cur = order.currency
  const total = Math.max(1, m.price_minor || order.price_minor || 1)
  const builderSide = order.role === 'builder'
  const parts = [
    {
      key: 'paid', label: builderSide ? 'Paid to you' : `Paid to ${firstName(order.builder.person, order.builder.name)}`,
      value: m.released_gross_minor, color: 'var(--color-success)',
      note: builderSide && m.paid_to_builder_minor !== m.released_gross_minor
        ? `${money(m.paid_to_builder_minor, cur)} after fees, tax and TDS` : '',
    },
    { key: 'held', label: 'Held by Twasta', value: m.held_minor, color: 'var(--color-accent)', note: '' },
    { key: 'due', label: 'Still to pay', value: m.still_to_pay_minor, color: 'var(--color-border-strong, var(--color-border))', note: '' },
  ]
  return (
    <div className="mkt-col" style={{ gap: 10 }}>
      <div className="mkt-stack" role="img" aria-label={parts.map((p) => `${p.label} ${money(p.value, cur)}`).join(', ')}>
        {parts.map((p) => (p.value > 0 ? <span key={p.key} style={{ width: `${Math.min(100, (p.value / total) * 100)}%`, background: p.color }} /> : null))}
      </div>
      <div className="mkt-row" style={{ gap: '8px 28px' }}>
        {parts.map((p) => (
          <span key={p.key} className="mkt-row" style={{ gap: 8, fontSize: 14 }}>
            <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: 3, background: p.color, display: 'inline-block' }} />
            <span className="mkt-muted">{p.label}</span>
            <strong>{money(p.value, cur)}</strong>
            {p.note && <span className="mkt-faint">({p.note})</span>}
          </span>
        ))}
        {m.refunded_minor > 0 && (
          <span className="mkt-row" style={{ gap: 8, fontSize: 14 }}>
            <span className="mkt-muted">Refunded</span><strong>{money(m.refunded_minor, cur)}</strong>
          </span>
        )}
      </div>
    </div>
  )
}

/** What Twasta measured in the builder's project — counted, never typed. */
export function MeasuredBlock({ m, title = 'Measured by Twasta' }: { m: Measured | null | undefined; title?: string }) {
  if (!m) return null
  const testsRun = (m.tests_passed || 0) + (m.tests_failed || 0)
  const rows: { l: string; v: string; s?: string; tone?: string }[] = [
    { l: 'Screens', v: String(m.screens ?? 0) },
    { l: 'Reports', v: String(m.reports ?? 0) },
    { l: 'Dashboards', v: String(m.dashboards ?? 0) },
    { l: 'Workflows', v: String(m.workflows ?? 0) },
    { l: 'Processes', v: String(m.processes ?? 0) },
    { l: 'Website pages', v: String(m.pages ?? 0) },
    {
      l: 'Tests', v: testsRun ? `${m.tests_passed} of ${testsRun} pass` : 'None run yet',
      s: testsRun ? `${m.tests_objects} screen${m.tests_objects === 1 ? '' : 's'} tested${m.tests_failed ? ` · ${m.tests_failed} failing` : ''}` : undefined,
      tone: m.tests_failed ? 'warn' : testsRun ? 'ok' : '',
    },
    { l: 'Builder activity', v: `${m.active_days ?? 0} of the last 7 days`, s: 'days the project changed' },
  ]
  return (
    <section className="mkt-col" style={{ gap: 10, border: '1px solid var(--color-border)', borderRadius: 14, padding: 16, background: 'var(--color-bg)' }} aria-label={title}>
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <strong>{title}</strong>
        <span className="mkt-faint">Counted from the project, not typed by the builder{m.at ? ` · ${fmtDateTime(m.at)}` : ''}</span>
      </div>
      {!m.readable && <span className="mkt-muted" style={{ fontSize: 14 }}>Twasta couldn’t read the project at that moment, so nothing was counted.</span>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
        {rows.map((r) => (
          <div key={r.l} className="mkt-col" style={{ gap: 2 }}>
            <span className="mkt-muted" style={{ fontSize: 13 }}>{r.l}</span>
            <strong style={{ fontSize: 18, color: r.tone === 'warn' ? 'var(--color-warning)' : r.tone === 'ok' ? 'var(--color-success)' : undefined }}>{r.v}</strong>
            {r.s && <span className="mkt-faint">{r.s}</span>}
          </div>
        ))}
      </div>
    </section>
  )
}

/** A labelled multi-line note (the inline panels' reason / note fields). */
export function NoteField({ id, label, value, onChange, placeholder, rows = 3, hint }: {
  id: string; label: string; value: string; onChange: (v: string) => void; placeholder?: string; rows?: number; hint?: string
}) {
  return (
    <div className="mkt-field">
      <label htmlFor={id}>{label}</label>
      <textarea id={id} rows={rows} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      {hint && <span className="mkt-faint">{hint}</span>}
    </div>
  )
}
